import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import logoFull from "@/assets/logo.png";
import logoIcon from "@/assets/logo-icon.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const nextPath = searchParams.get("next") || "/editions";

  useEffect(() => {
    if (user) navigate(nextPath);
  }, [user, navigate, nextPath]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      navigate(nextPath);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative">
      <SEOHead title="Login — aibly" description="Log in to your aibly account to manage your daily AI newsletter, delivery time, and reading history." canonical="/login" noindex />
      <Link
        to="/"
        className="absolute top-5 left-5 flex items-center gap-1 text-xs tracking-wider uppercase text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span>Home</span>
      </Link>
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center mb-6">
            <img src={logoIcon} alt="aibly logo" width={500} height={500} className="h-16 w-auto dark:invert sm:hidden" />
            <img src={logoFull} alt="aibly logo" width={486} height={135} className="h-10 w-auto dark:invert hidden sm:block" />
          </div>
          <p className="text-[10px] tracking-[0.4em] uppercase text-accent font-mono">Returning reader</p>
          <h1 className="font-display text-2xl italic text-foreground">
            Welcome back.
          </h1>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <Input
            aria-label="Email address"
            type="email"
            placeholder="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm"
          />
          <Input
            aria-label="Password"
            type="password"
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm"
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-none text-xs tracking-widest uppercase"
          >
            {loading ? "Authenticating..." : "Login"}
          </Button>
        </form>

        <div className="text-center space-y-2">
          <Link
            to="/forgot-password"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors tracking-wider uppercase"
          >
            Forgot password?
          </Link>
          <p className="text-xs text-muted-foreground">
            No account?{" "}
            <Link to="/signup" className="text-foreground hover:text-accent transition-colors">
              Subscribe free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
